// Aignoz Cap Table Visualizer — /captable (Phase 6).
// Donut ownership · stakeholder table with vesting bars · rounds timeline · simulate mode.
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  PieChart, Pie, Cell, Tooltip as RTooltip, ResponsiveContainer,
} from "recharts";
import {
  Plus, Trash2, Edit2, Download, FileText, Save, Sparkles, Loader2, X, Link2,
  Users, TrendingUp, Building2, Award, User, Handshake, CheckCircle2, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const MAGENTA = "#D17EC0";
const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TYPES = [
  { key: "founder",     label: "Founder",     color: "#D17EC0", icon: User },
  { key: "co_founder",  label: "Co-Founder",  color: "#34D399", icon: Users },
  { key: "investor",    label: "Investor",    color: "#22D3EE", icon: TrendingUp },
  { key: "employee",    label: "Employee",    color: "#FBBF24", icon: Award },
  { key: "advisor",     label: "Advisor",     color: "#A78BFA", icon: Handshake },
  { key: "safe_holder", label: "SAFE Holder", color: "#94A3B8", icon: Building2 },
];
const TYPE_COLOR = Object.fromEntries(TYPES.map((t) => [t.key, t.color]));
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.key, t.label]));

function fmtInt(n) { return Number(n || 0).toLocaleString("en-IN"); }
function fmtUsd(n) {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (Math.abs(v) >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${Math.round(v)}`;
}

// ===========================================================================
// PAGE
// ===========================================================================
export default function CapTablePage() {
  const [tables, setTables] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [renameBusy, setRenameBusy] = useState(false);
  const [showAddSh, setShowAddSh] = useState(false);
  const [editSh, setEditSh] = useState(null);
  const [showAddRound, setShowAddRound] = useState(false);
  const [simulateOn, setSimulateOn] = useState(false);
  const [simInputs, setSimInputs] = useState({ name: "Series A", pre_money_valuation_usd: 8_000_000, amount_raised_usd: 2_000_000, esop_top_up_pct: 10 });
  const [simPreview, setSimPreview] = useState(null);
  const [simBusy, setSimBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // ---------- fetch cap-table list ----------
  const fetchTables = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/captable/tables`);
      setTables(data || []);
      if (data && data.length && !activeId) setActiveId(data[0].id);
    } catch (e) {
      toast.error("Could not load cap tables", { description: e.message });
    } finally {
      setLoading(false);
    }
  }, [activeId]);

  useEffect(() => { fetchTables(); /* eslint-disable-next-line */ }, []);

  // ---------- fetch active table ----------
  useEffect(() => {
    if (!activeId) { setTable(null); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/captable/tables/${activeId}`);
        setTable(data);
        setSimPreview(null);
      } catch (e) {
        toast.error("Could not open cap table", { description: e.message });
      }
    })();
  }, [activeId]);

  // ---------- rename ----------
  async function renameTable(newName) {
    if (!table) return;
    setRenameBusy(true);
    try {
      const { data } = await axios.patch(`${API}/captable/tables/${table.id}`, { name: newName });
      setTable(data);
      setTables((t) => t.map((x) => (x.id === data.id ? { ...x, name: data.name } : x)));
    } catch (e) {
      toast.error("Rename failed", { description: e.message });
    } finally { setRenameBusy(false); }
  }

  // ---------- create ----------
  async function createTable(name) {
    try {
      const { data } = await axios.post(`${API}/captable/tables`, { name });
      await fetchTables();
      setActiveId(data.id);
      toast.success("Created", { description: name });
    } catch (e) {
      toast.error("Create failed", { description: e.message });
    }
  }

  // ---------- stakeholder ops ----------
  async function addStakeholder(payload) {
    try {
      const { data } = await axios.post(`${API}/captable/tables/${table.id}/stakeholders`, payload);
      setTable(data); setSimPreview(null);
      toast.success("Stakeholder added", { description: payload.name });
    } catch (e) {
      toast.error("Add failed", { description: e.response?.data?.detail || e.message });
    }
  }
  async function updateStakeholder(id, payload) {
    try {
      const { data } = await axios.patch(`${API}/captable/tables/${table.id}/stakeholders/${id}`, payload);
      setTable(data); setSimPreview(null);
    } catch (e) {
      toast.error("Update failed", { description: e.message });
    }
  }
  async function deleteStakeholder(id) {
    try {
      const { data } = await axios.delete(`${API}/captable/tables/${table.id}/stakeholders/${id}`);
      setTable(data); setSimPreview(null);
    } catch (e) {
      toast.error("Delete failed", { description: e.message });
    }
  }

  // ---------- round ops ----------
  async function addRound(payload) {
    try {
      const { data } = await axios.post(`${API}/captable/tables/${table.id}/rounds`, payload);
      setTable(data);
      toast.success("Round added", { description: payload.name });
    } catch (e) {
      toast.error("Round add failed", { description: e.message });
    }
  }

  // ---------- simulate ----------
  async function runSimulate() {
    setSimBusy(true);
    try {
      const { data } = await axios.post(`${API}/captable/tables/${table.id}/simulate`, { round: simInputs });
      setSimPreview(data);
    } catch (e) {
      toast.error("Simulate failed", { description: e.message });
    } finally { setSimBusy(false); }
  }

  async function commitSimulated() {
    if (!simPreview) return;
    await addRound({ ...simInputs, type: "priced", is_hypothetical: false });
    setSimPreview(null);
    setSimulateOn(false);
  }

  // ---------- export ----------
  async function download(kind /* "csv" | "pdf" */) {
    if (!table) return;
    try {
      const res = await axios.get(`${API}/captable/tables/${table.id}/export.${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `aignoz-captable-${table.id.slice(0, 8)}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(`Export ${kind.toUpperCase()} failed`, { description: e.message });
    }
  }

  const stakeholders = table?.stakeholders || [];
  const rounds = table?.rounds || [];
  const totalShares = stakeholders.reduce((s, x) => s + (x.shares || 0), 0);

  // ---------- render ----------
  return (
    <div className="h-full overflow-y-auto" data-testid="captable-page">
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        <Header
          tables={tables}
          activeId={activeId}
          onSelect={setActiveId}
          onCreate={() => setShowCreate(true)}
          onRename={renameTable}
          onDownloadCsv={() => download("csv")}
          onDownloadPdf={() => download("pdf")}
          renameBusy={renameBusy}
          table={table}
        />

        {loading ? (
          <SkeletonBody />
        ) : !table ? (
          <div className="mt-10 text-white/45 italic text-sm">No cap table yet. Create one to get started.</div>
        ) : (
          <>
            {/* Ownership + Table row */}
            <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-5 mt-6">
              <DonutCard
                testId="captable-donut-chart"
                stakeholders={stakeholders}
                totalShares={totalShares}
                title="Current Ownership"
              />
              <StakeholderTable
                stakeholders={stakeholders}
                totalShares={totalShares}
                onAdd={() => setShowAddSh(true)}
                onEdit={(sh) => setEditSh(sh)}
                onDelete={deleteStakeholder}
              />
            </div>

            {/* Rounds timeline */}
            <RoundsTimeline rounds={rounds} onAdd={() => setShowAddRound(true)} />

            {/* Simulate mode */}
            <SimulateBar
              on={simulateOn}
              onToggle={setSimulateOn}
              inputs={simInputs}
              setInputs={setSimInputs}
              onPreview={runSimulate}
              onCommit={commitSimulated}
              hasPreview={!!simPreview}
              busy={simBusy}
            />
            <AnimatePresence>
              {simPreview && simulateOn && (
                <SimulatePreview
                  preview={simPreview}
                  currentStakeholders={stakeholders}
                />
              )}
            </AnimatePresence>
          </>
        )}

        <div className="mt-14 text-center text-[10px] text-white/30">
          Designed by <span className="text-white/55">Dr. Sriinidhi BV (MBBS, MD)</span> · Aignoz Innovation OS™
        </div>
      </div>

      {/* Modals */}
      <CreateTableModal open={showCreate} onOpenChange={setShowCreate} onCreate={createTable} />
      <StakeholderForm
        open={showAddSh || !!editSh}
        editing={editSh}
        onOpenChange={(v) => { if (!v) { setShowAddSh(false); setEditSh(null); } }}
        onSubmit={async (payload) => {
          if (editSh) await updateStakeholder(editSh.id, payload);
          else        await addStakeholder(payload);
          setShowAddSh(false); setEditSh(null);
        }}
      />
      <AddRoundModal open={showAddRound} onOpenChange={setShowAddRound}
        onSubmit={async (p) => { await addRound(p); setShowAddRound(false); }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
function Header({ tables, activeId, onSelect, onCreate, onRename, onDownloadCsv, onDownloadPdf, renameBusy, table }) {
  const [editingName, setEditingName] = useState(false);
  const [draft, setDraft] = useState("");
  useEffect(() => { setDraft(table?.name || ""); }, [table?.id, table?.name]);
  return (
    <header className="flex flex-wrap items-center gap-3">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mono">Aignoz Idea Factory · Phase 6</div>
        <h1 className="text-2xl font-semibold tracking-tight">Cap Table Visualizer</h1>
      </div>
      <div className="flex items-center gap-2 ml-auto">
        <div data-testid="captable-selector" className="min-w-[260px]">
          <Select value={activeId || ""} onValueChange={onSelect}>
            <SelectTrigger><SelectValue placeholder="Select cap table…" /></SelectTrigger>
            <SelectContent>
              {tables.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={onCreate} data-testid="captable-create-btn" className="gap-1.5">
          <Plus className="w-3 h-3" /> Create new
        </Button>
        <Button variant="outline" size="sm" onClick={onDownloadCsv} data-testid="captable-export-csv-btn" className="gap-1.5">
          <Download className="w-3 h-3" /> CSV
        </Button>
        <Button variant="outline" size="sm" onClick={onDownloadPdf} data-testid="captable-export-pdf-btn" className="gap-1.5">
          <FileText className="w-3 h-3" /> PDF
        </Button>
      </div>

      {table && (
        <div className="w-full mt-3 flex items-center gap-2">
          {editingName ? (
            <>
              <Input
                data-testid="captable-name-input"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                className="max-w-[540px]"
              />
              <Button size="sm" onClick={async () => { await onRename(draft); setEditingName(false); }} disabled={renameBusy}>
                {renameBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setDraft(table.name); setEditingName(false); }}>
                Cancel
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold tracking-tight text-white/90 truncate">{table.name}</h2>
              <button onClick={() => setEditingName(true)} className="text-white/40 hover:text-white/80 text-[10px] mono uppercase tracking-[0.14em] inline-flex items-center gap-1">
                <Edit2 className="w-3 h-3" /> Rename
              </button>
              {table.linked_run_id && (
                <span data-testid="captable-link-venture-btn" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 rounded-full border border-teal-400/40 text-teal-200 bg-teal-500/[0.06]">
                  <Link2 className="w-3 h-3" /> {table.linked_run_id.slice(0, 8)}…
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </header>
  );
}

// ---------------------------------------------------------------------------
function DonutCard({ testId, stakeholders, totalShares, title }) {
  const data = useMemo(() => stakeholders.map((s) => ({
    id: s.id, name: s.name, type: s.type,
    value: s.shares, percent: s.ownership?.percent || 0,
    color: TYPE_COLOR[s.type] || "#94A3B8",
  })), [stakeholders]);
  return (
    <div data-testid={testId} className="rounded-xl border border-white/[0.08] bg-white/[0.012] p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">{title}</div>
        <span className="text-[10px] mono text-white/40">{stakeholders.length} stakeholders · {fmtInt(totalShares)} shares</span>
      </div>
      <div className="h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={70} outerRadius={110} paddingAngle={1} stroke="rgba(11,12,16,0.6)" strokeWidth={2}>
              {data.map((d) => <Cell key={d.id} fill={d.color} />)}
            </Pie>
            <RTooltip
              wrapperStyle={{ zIndex: 100 }}
              contentStyle={{ background: "rgba(11,12,16,0.92)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 8, color: "#fff" }}
              formatter={(value, _name, o) => [
                `${fmtInt(value)} shares · ${(o.payload.percent || 0).toFixed(2)}%`,
                o.payload.name,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px]">
        {TYPES.map((t) => (
          <div key={t.key} className="inline-flex items-center gap-1.5 text-white/60">
            <span className="w-2 h-2 rounded-full" style={{ background: t.color }} /> {t.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function StakeholderTable({ stakeholders, totalShares, onAdd, onEdit, onDelete }) {
  return (
    <div data-testid="captable-stakeholder-table" className="rounded-xl border border-white/[0.08] bg-white/[0.012] p-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">Stakeholders</div>
        <Button size="sm" variant="outline" onClick={onAdd} data-testid="captable-add-stakeholder-btn" className="gap-1.5 h-7 text-[10px] uppercase tracking-[0.12em]">
          <Plus className="w-3 h-3" /> Add
        </Button>
      </div>
      <div className="space-y-2 overflow-y-auto max-h-[420px] pr-1">
        {stakeholders.length === 0 && (
          <div className="text-[12px] text-white/40 italic text-center py-8">Empty cap table.</div>
        )}
        {stakeholders.map((s) => (
          <StakeholderRow key={s.id} s={s} totalShares={totalShares} onEdit={onEdit} onDelete={onDelete} />
        ))}
      </div>
    </div>
  );
}

function StakeholderRow({ s, totalShares, onEdit, onDelete }) {
  const meta = TYPES.find((t) => t.key === s.type) || TYPES[0];
  const pct = s.ownership?.percent || (totalShares ? (s.shares / totalShares) * 100 : 0);
  const vestedPct = s.vested?.vested_pct ?? 100;
  const Icon = meta.icon;
  return (
    <div data-testid={`captable-stakeholder-row-${s.id}`} className="rounded-md border border-white/[0.06] bg-white/[0.015] px-3 py-2.5">
      <div className="flex items-center gap-2">
        <span
          className="w-6 h-6 rounded inline-flex items-center justify-center shrink-0"
          style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}66`, color: meta.color }}
        ><Icon className="w-3 h-3" /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium text-white/95 truncate">{s.name}</div>
          <div className="text-[10px] text-white/40 mono uppercase tracking-[0.1em]">{meta.label}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-semibold" style={{ color: meta.color }}>{pct.toFixed(2)}%</div>
          <div className="text-[10px] text-white/45 mono">{fmtInt(s.shares)} sh</div>
        </div>
        <div className="flex gap-1 shrink-0 ml-2">
          <button onClick={() => onEdit(s)} className="p-1 text-white/40 hover:text-white/85" title="Edit">
            <Edit2 className="w-3 h-3" />
          </button>
          <button onClick={() => onDelete(s.id)} className="p-1 text-white/40 hover:text-rose-300" title="Delete">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
      {s.vesting?.enabled && (
        <div className="mt-2" title={s.vested?.next_vest_date ? `Next vest: ${s.vested.next_vest_date.slice(0, 10)}` : ""}>
          <div className="h-1 w-full rounded bg-white/[0.06] overflow-hidden">
            <div className="h-full rounded" style={{ width: `${vestedPct}%`, background: meta.color, opacity: 0.7 }} />
          </div>
          <div className="mt-1 text-[9px] mono text-white/40 uppercase tracking-[0.1em]">
            {vestedPct.toFixed(0)}% vested · {s.vesting.cliff_months}mo cliff · {s.vesting.total_months}mo total
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function RoundsTimeline({ rounds, onAdd }) {
  return (
    <div data-testid="captable-rounds-timeline" className="mt-5 rounded-xl border border-white/[0.08] bg-white/[0.012] p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">Rounds</div>
        <Button size="sm" variant="outline" data-testid="captable-add-round-btn" onClick={onAdd} className="gap-1.5 h-7 text-[10px] uppercase tracking-[0.12em]">
          <Plus className="w-3 h-3" /> Add real round
        </Button>
      </div>
      {rounds.length === 0 ? (
        <div className="text-[12px] text-white/40 italic">No funding rounds recorded yet.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {rounds.map((r) => (
            <div key={r.id}
                 className="inline-flex items-center gap-2 px-3 py-2 rounded-md border text-[12px]"
                 style={{ borderColor: r.is_hypothetical ? "rgba(251,191,36,0.35)" : "rgba(94,234,212,0.35)",
                          background: r.is_hypothetical ? "rgba(251,191,36,0.05)" : "rgba(94,234,212,0.04)" }}>
              <span className="font-semibold" style={{ color: r.is_hypothetical ? "#FBBF24" : "#5EEAD4" }}>{r.name}</span>
              <span className="text-white/60">{fmtUsd(r.amount_raised_usd)}</span>
              <span className="text-white/40">@ {fmtUsd(r.pre_money_valuation_usd)} pre</span>
              {r.esop_top_up_pct > 0 && <span className="text-white/45">+ ESOP {r.esop_top_up_pct}%</span>}
              {r.is_hypothetical && <span className="text-amber-300 text-[9px] mono uppercase tracking-[0.14em]">Sim</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function SimulateBar({ on, onToggle, inputs, setInputs, onPreview, onCommit, hasPreview, busy }) {
  return (
    <div className="mt-5 rounded-xl border border-white/[0.08] bg-white/[0.012] p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-0.5">Simulation Mode</div>
          <div className="text-[12px] text-white/60 italic">Preview dilution for a hypothetical priced round.</div>
        </div>
        <label className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.14em] text-white/50">{on ? "ON" : "OFF"}</span>
          <Switch checked={on} onCheckedChange={onToggle} data-testid="captable-simulate-toggle" />
        </label>
      </div>
      <AnimatePresence>
        {on && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}
            data-testid="captable-simulate-form"
            className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-2 overflow-hidden"
          >
            <FormField label="Round name">
              <Input value={inputs.name} onChange={(e) => setInputs({ ...inputs, name: e.target.value })} />
            </FormField>
            <FormField label="Pre-money USD">
              <Input type="number" value={inputs.pre_money_valuation_usd}
                     onChange={(e) => setInputs({ ...inputs, pre_money_valuation_usd: Number(e.target.value) })} />
            </FormField>
            <FormField label="Amount raised USD">
              <Input type="number" value={inputs.amount_raised_usd}
                     onChange={(e) => setInputs({ ...inputs, amount_raised_usd: Number(e.target.value) })} />
            </FormField>
            <FormField label="ESOP top-up %">
              <Input type="number" min={0} max={30} value={inputs.esop_top_up_pct}
                     onChange={(e) => setInputs({ ...inputs, esop_top_up_pct: Number(e.target.value) })} />
            </FormField>
            <div className="md:col-span-4 flex items-center gap-2 mt-2">
              <Button onClick={onPreview} disabled={busy} data-testid="captable-simulate-preview-btn" className="gap-2 bg-teal-500 hover:bg-teal-400 text-black">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Preview dilution
              </Button>
              {hasPreview && (
                <Button variant="outline" onClick={onCommit} data-testid="captable-simulate-commit-btn" className="gap-2" style={{ borderColor: `${MAGENTA}80`, color: MAGENTA }}>
                  <CheckCircle2 className="w-4 h-4" /> Commit to Real
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <div className="text-[9px] mono uppercase tracking-[0.14em] text-white/45 mb-1">{label}</div>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
function SimulatePreview({ preview, currentStakeholders }) {
  const beforeData = (preview.before || []).map((s) => ({ ...s, color: TYPE_COLOR[s.type] || "#94A3B8" }));
  const afterData = (preview.after || []).map((s) => ({ ...s, color: TYPE_COLOR[s.type] || "#94A3B8" }));
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
      className="mt-5 rounded-xl border border-white/[0.08] bg-white/[0.012] p-4"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-3">
        Preview · {preview.round_name} · {fmtUsd(preview.new_investor_shares * preview.price_per_share || 0)} at ${preview.price_per_share.toFixed(4)}/share
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MiniDonut testId="captable-before-donut" title="Before" data={beforeData} total={preview.current_total_shares} />
        <MiniDonut testId="captable-after-donut"  title="After"  data={afterData}  total={preview.post_money_total_shares} />
      </div>

      <div className="mt-4" data-testid="captable-dilution-table">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-2">Dilution table</div>
        <div className="text-[11px] rounded-md border border-white/[0.06] overflow-hidden">
          <div className="grid grid-cols-[1fr_100px_100px_100px] px-3 py-1.5 bg-white/[0.03] text-white/50 mono uppercase tracking-[0.1em] text-[9px]">
            <div>Name</div><div className="text-right">Before</div><div className="text-right">After</div><div className="text-right">Δ</div>
          </div>
          {(preview.dilution || []).map((d, i) => {
            const delta = d.delta || 0;
            const dilutedHard = delta < 0 && Math.abs(delta) >= 20 * (d.percent_before / 100);
            const color = d.is_new ? "#5EEAD4" : (delta >= 0 ? "#94A3B8" : dilutedHard ? MAGENTA : "#F87171");
            return (
              <div key={d.id} className={`grid grid-cols-[1fr_100px_100px_100px] px-3 py-1.5 ${i % 2 ? "bg-white/[0.02]" : ""}`}>
                <div className="text-white/85 truncate">{d.name}{d.is_new && <span className="ml-2 text-[9px] mono uppercase tracking-[0.14em] text-teal-300">new</span>}</div>
                <div className="text-right text-white/70 mono">{(d.percent_before || 0).toFixed(2)}%</div>
                <div className="text-right text-white/95 mono">{(d.percent_after || 0).toFixed(2)}%</div>
                <div className="text-right mono" style={{ color }}>
                  {delta > 0 ? "+" : ""}{delta.toFixed(2)}%
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
}

function MiniDonut({ testId, title, data, total }) {
  return (
    <div data-testid={testId} className="rounded-md border border-white/[0.06] bg-white/[0.014] p-3">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-[0.14em] text-white/50">{title}</div>
        <div className="text-[10px] mono text-white/40">{fmtInt(total)} sh</div>
      </div>
      <div className="h-[190px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="shares" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={1} stroke="rgba(11,12,16,0.6)" strokeWidth={2}>
              {data.map((d) => <Cell key={d.id} fill={d.color} />)}
            </Pie>
            <RTooltip
              contentStyle={{ background: "rgba(11,12,16,0.92)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 8, color: "#fff" }}
              formatter={(v, _n, o) => [`${fmtInt(v)} · ${(o.payload.percent || 0).toFixed(2)}%`, o.payload.name]}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function SkeletonBody() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-5 mt-6">
      <div className="h-[360px] rounded-xl border border-white/[0.06] bg-white/[0.014] animate-pulse" />
      <div className="h-[360px] rounded-xl border border-white/[0.06] bg-white/[0.014] animate-pulse" />
    </div>
  );
}

// ---------------------------------------------------------------------------
function CreateTableModal({ open, onOpenChange, onCreate }) {
  const [name, setName] = useState("New Cap Table");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create new cap table</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} /></FormField>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={async () => { await onCreate(name); onOpenChange(false); }} disabled={!name.trim()}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StakeholderForm({ open, onOpenChange, onSubmit, editing }) {
  const [form, setForm] = useState({
    name: "", type: "founder", shares: 0,
    vesting: { enabled: true, cliff_months: 12, total_months: 48 },
    notes: "",
  });
  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name || "", type: editing.type || "founder",
        shares: editing.shares || 0,
        vesting: editing.vesting || { enabled: true, cliff_months: 12, total_months: 48 },
        notes: editing.notes || "",
      });
    } else {
      setForm({ name: "", type: "founder", shares: 0, vesting: { enabled: true, cliff_months: 12, total_months: 48 }, notes: "" });
    }
  }, [editing, open]);
  const canSubmit = form.name.trim() && form.shares > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="captable-add-stakeholder-form">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit stakeholder" : "Add stakeholder"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <FormField label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Type">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Shares">
              <Input type="number" value={form.shares} onChange={(e) => setForm({ ...form, shares: Number(e.target.value) })} />
            </FormField>
          </div>
          <div className="flex items-center justify-between pt-1">
            <Label className="text-[12px]">Vesting</Label>
            <Switch
              checked={!!form.vesting?.enabled}
              onCheckedChange={(v) => setForm({ ...form, vesting: { ...form.vesting, enabled: v } })}
            />
          </div>
          {form.vesting?.enabled && (
            <div className="grid grid-cols-2 gap-2">
              <FormField label="Cliff (months)">
                <Input type="number" value={form.vesting?.cliff_months || 0}
                  onChange={(e) => setForm({ ...form, vesting: { ...form.vesting, cliff_months: Number(e.target.value) } })} />
              </FormField>
              <FormField label="Total (months)">
                <Input type="number" value={form.vesting?.total_months || 0}
                  onChange={(e) => setForm({ ...form, vesting: { ...form.vesting, total_months: Number(e.target.value) } })} />
              </FormField>
            </div>
          )}
          <FormField label="Notes (optional)">
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!canSubmit} onClick={() => onSubmit(form)}>
            {editing ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddRoundModal({ open, onOpenChange, onSubmit }) {
  const [form, setForm] = useState({ name: "Seed", pre_money_valuation_usd: 4_000_000, amount_raised_usd: 1_000_000, esop_top_up_pct: 0, is_hypothetical: false, type: "priced" });
  const canSubmit = form.name.trim() && form.amount_raised_usd > 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Add real round</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <FormField label="Round name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
          <div className="grid grid-cols-2 gap-2">
            <FormField label="Pre-money USD"><Input type="number" value={form.pre_money_valuation_usd} onChange={(e) => setForm({ ...form, pre_money_valuation_usd: Number(e.target.value) })} /></FormField>
            <FormField label="Amount raised USD"><Input type="number" value={form.amount_raised_usd} onChange={(e) => setForm({ ...form, amount_raised_usd: Number(e.target.value) })} /></FormField>
          </div>
          <FormField label="ESOP top-up %"><Input type="number" min={0} max={30} value={form.esop_top_up_pct} onChange={(e) => setForm({ ...form, esop_top_up_pct: Number(e.target.value) })} /></FormField>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!canSubmit} onClick={() => onSubmit(form)}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
